import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { dealSourceFilesKeys } from '@/hooks/query-keys'
import * as sourceFilesService from '@/services/deal-source-files.service'
import type { DealEmailBody } from '@/types/api'

export { dealSourceFilesKeys } from '@/hooks/query-keys'

export function useDealEmailBody(sourceFileId: string | undefined) {
  return useQuery({
    queryKey: dealSourceFilesKeys.emailBody(sourceFileId ?? ''),
    queryFn: () => sourceFilesService.getDealEmailBody(sourceFileId as string),
    enabled: Boolean(sourceFileId),
  })
}

// Only this exact shape is resolved; anything else the sanitizer let through just fails to load.
const INLINE_IMAGE_PATH = /^\/deal-source-files\/([0-9a-fA-F-]{36})\/content$/

function toDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () =>
      reject(reader.error ?? new Error('Could not read the inline image bytes'))
    reader.readAsDataURL(blob)
  })
}

// Data URIs, not blob URLs: the sandboxed frame has an opaque origin and can't read blob: URLs.
export function useResolvedEmailHtml(body: DealEmailBody | undefined): {
  html: string | null
  unresolvedImages: number
} {
  // Keyed by body so a new email derives "loading" without an effect resetting state.
  const [resolved, setResolved] = useState<{
    body: DealEmailBody
    html: string
    unresolvedImages: number
  } | null>(null)

  useEffect(() => {
    if (!body) return

    let cancelled = false

    const resolve = async () => {
      const parsed = new DOMParser().parseFromString(body.html, 'text/html')
      let failed = 0

      for (const image of parsed.querySelectorAll('img')) {
        const match = INLINE_IMAGE_PATH.exec(image.getAttribute('src') ?? '')
        if (!match) continue

        try {
          const blob = await sourceFilesService.getDealSourceFileBlob(match[1])
          if (cancelled) return
          image.setAttribute('src', await toDataUrl(blob))
        } catch {
          // One unreadable image (e.g. past 180-day retention) must not blank the whole email.
          failed += 1
          image.removeAttribute('src')
          image.setAttribute('alt', 'This inline image could not be loaded.')
        }
      }

      if (cancelled) return
      stripSpacers(parsed)
      setResolved({
        body,
        html: parsed.body.innerHTML,
        unresolvedImages: failed,
      })
    }

    void resolve()

    return () => {
      cancelled = true
    }
  }, [body])

  if (!body || resolved === null || resolved.body !== body) {
    return { html: null, unresolvedImages: 0 }
  }
  return { html: resolved.html, unresolvedImages: resolved.unresolvedImages }
}

// Outlook pads blank lines with empty <p>s that render full height once the API strips styles.
function stripSpacers(parsed: Document): void {
  for (const element of [...parsed.querySelectorAll('p, div')]) {
    if (element.querySelector('img, table, hr')) continue
    if ((element.textContent ?? '').replace(/[\s\u00a0]+/g, '') !== '') continue
    element.remove()
  }
}

// SVG is excluded: a same-origin blob URL opened in a tab would run its script.
export const PREVIEWABLE_IMAGE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
]

export function isPreviewableImage(mimeType: string | null | undefined) {
  return PREVIEWABLE_IMAGE_TYPES.includes(mimeType?.toLowerCase() ?? '')
}

export function useSourceFileImageUrl(
  sourceFileId: string | undefined,
  mimeType: string | null | undefined
): {
  url: string | null
  isLoading: boolean
  isError: boolean
} {
  const blob = useQuery({
    queryKey: dealSourceFilesKeys.content(sourceFileId ?? ''),
    // `enabled` guarantees an id; throwing narrows the type without a cast.
    queryFn: () => {
      if (sourceFileId === undefined) {
        throw new Error('A source file id is required to read its bytes')
      }
      return sourceFilesService.getDealSourceFileBlob(sourceFileId)
    },
    enabled: Boolean(sourceFileId),
  })

  // Re-typed so the object URL never carries the server's declared type.
  const url = useMemo(
    () =>
      blob.data && isPreviewableImage(mimeType)
        ? URL.createObjectURL(
            new Blob([blob.data], { type: mimeType?.toLowerCase() })
          )
        : null,
    [blob.data, mimeType]
  )

  // The blob stays cached but the URL is per mount; revoke it or it leaks.
  useEffect(() => {
    if (url === null) return
    return () => URL.revokeObjectURL(url)
  }, [url])

  return { url, isLoading: blob.isPending, isError: blob.isError }
}

export type SourceFileDownloadState = 'idle' | 'loading' | 'error'

// Download, not window.open: after an await, popup blockers stop window.open.
export function useSourceFileDownload() {
  const [states, setStates] = useState<
    Record<string, SourceFileDownloadState | undefined>
  >({})

  const download = useCallback(
    async (sourceFileId: string, filename: string | undefined) => {
      setStates((current) => ({ ...current, [sourceFileId]: 'loading' }))
      try {
        const blob =
          await sourceFilesService.getDealSourceFileBlob(sourceFileId)
        const url = URL.createObjectURL(blob)
        const anchor = document.createElement('a')
        anchor.href = url
        anchor.download = filename ?? sourceFileId
        document.body.append(anchor)
        anchor.click()
        anchor.remove()
        // Revoking in the same tick can cancel the download in some browsers.
        setTimeout(() => URL.revokeObjectURL(url), 0)
        setStates((current) => ({ ...current, [sourceFileId]: 'idle' }))
      } catch {
        setStates((current) => ({ ...current, [sourceFileId]: 'error' }))
      }
    },
    []
  )

  return { download, states }
}
